import { useEffect, useState } from "react";
import { alpha, createTheme, type Theme } from "@mui/material/styles";
import { useAppStore } from "./shared/store/app";

export type ResolvedTheme = "light" | "dark";

/** The shell palette (spec §8). One deep-teal accent, no second identity. */
export const SHELL_PALETTE: Record<
  ResolvedTheme,
  {
    primary: string;
    contrastText: string;
    background: string;
    paper: string;
    divider: string;
  }
> = {
  light: {
    primary: "#0f766e",
    contrastText: "#ffffff",
    background: "#f4f6f7",
    paper: "#ffffff",
    divider: "rgba(0, 0, 0, 0.08)",
  },
  dark: {
    primary: "#5eead4",
    contrastText: "#042f2e",
    background: "#0f1315",
    paper: "#171c1f",
    divider: "rgba(255, 255, 255, 0.1)",
  },
};

/**
 * The phone content title. Tablet/desktop titles use the theme's `h1`; the
 * compact shell keeps the same weight one step down (spec §8).
 */
export const COMPACT_PAGE_TITLE_SIZE = "1.3rem";

/**
 * Minimum interactive target on touch (spec §10). Applied as a theme default on
 * coarse pointers so compact visuals still carry a 44px hit area.
 */
export const MIN_TARGET = 44;

/**
 * Fine-pointer (mouse/trackpad) targets — spec §10 amendment (2026-10-27).
 * Compact, but still above WCAG 2.5.8's 24px AA minimum.
 */
export const MIN_TARGET_FINE = 32;
export const MIN_ICON_TARGET_FINE = 36;

/** Height target: compact for a mouse, 44px once the pointer is coarse. */
const POINTER_HEIGHT = {
  minHeight: MIN_TARGET_FINE,
  "@media (pointer: coarse)": { minHeight: MIN_TARGET },
} as const;

/** Two-axis target (icon buttons, pagination): 36px fine, 44px coarse. */
const POINTER_BOX = {
  minWidth: MIN_ICON_TARGET_FINE,
  minHeight: MIN_ICON_TARGET_FINE,
  "@media (pointer: coarse)": { minWidth: MIN_TARGET, minHeight: MIN_TARGET },
} as const;

const BASE_RADIUS = 4;
const ICON_BUTTON_RADIUS = 6;
const OVERLAY_RADIUS = 8;

/** No elevation: surfaces are separated by hairlines only (spec §8). */
const FLAT_SHADOWS = Array.from({ length: 25 }, () => "none");

/** The shell background; also the browser's theme-color (spec §8). */
export function shellBackground(mode: ResolvedTheme): string {
  return SHELL_PALETTE[mode].background;
}

/** Full-cell selection tint: 10% light / 16% dark (spec §2, §8). */
export function selectedTint(theme: Theme): string {
  return alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.16 : 0.1);
}

/**
 * Keeps `<meta name="theme-color">` on the shell background so the browser
 * chrome matches the app in both modes (spec §8).
 *
 * @param mode the resolved light or dark theme.
 */
export function applyThemeColor(mode: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", shellBackground(mode));
}

/**
 * Builds the one app theme: deep-teal primary, flat hairline surfaces, the
 * comfortable type scale and the 4/6/8px control shapes (spec §8).
 *
 * @param mode the resolved light or dark theme.
 * @returns a fresh MUI theme for that mode.
 */
export function buildTheme(mode: ResolvedTheme): Theme {
  const shell = SHELL_PALETTE[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: shell.primary, contrastText: shell.contrastText },
      background: { default: shell.background, paper: shell.paper },
      divider: shell.divider,
    },
    shape: { borderRadius: BASE_RADIUS },
    typography: {
      h1: { fontSize: "1.75rem", fontWeight: 650, lineHeight: 1.2 },
      h6: { fontSize: "1.0625rem", fontWeight: 650 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontSize: "0.9375rem", lineHeight: 1.5 },
      body2: { fontSize: "0.875rem", lineHeight: 1.45 },
      caption: { fontSize: "0.72rem", letterSpacing: "0.01em" },
      button: { fontWeight: 600, letterSpacing: 0, textTransform: "none" },
    },
    shadows: FLAT_SHADOWS as Theme["shadows"],
    components: {
      // Targets, not looks (spec §10 + the 2026-10-27 pointer amendment): a
      // mouse gets compact 32/36px controls, a finger keeps the full 44px
      // target. Clickable chips follow the same rule; display-only badges never
      // got a target in the first place.
      MuiButton: { styleOverrides: { root: { ...POINTER_HEIGHT } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: BASE_RADIUS },
          clickable: { ...POINTER_HEIGHT },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: ICON_BUTTON_RADIUS,
            ...POINTER_BOX,
          },
        },
      },
      MuiPaginationItem: { styleOverrides: { root: { ...POINTER_BOX } } },
      MuiRadio: {
        styleOverrides: {
          root: {
            width: MIN_ICON_TARGET_FINE,
            height: MIN_ICON_TARGET_FINE,
            "@media (pointer: coarse)": { width: MIN_TARGET, height: MIN_TARGET },
          },
        },
      },
      MuiToggleButton: { styleOverrides: { root: { ...POINTER_HEIGHT } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: OVERLAY_RADIUS } } },
      // The search field stays outlined and square: the base radius, never a
      // filled or pill input (spec §8).
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: BASE_RADIUS } } },
      // Dark-mode Paper otherwise layers a tonal elevation overlay on top of
      // the flat surfaces; the hairline rules are the only separation.
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    },
  });
}

/** Resolves the effective theme, honouring the "system" preference (ADR-0001). */
export function useResolvedTheme(): ResolvedTheme {
  const mode = useAppStore((s) => s.mode);
  const [system, setSystem] = useState<ResolvedTheme>(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  );

  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  return mode === "system" ? system : mode;
}
